import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './App.css';
import { Helmet } from 'react-helmet';
import CircleAnimation from './components/CircleAnimation';

const API_URL = "https://ampyfin-website-pyj4.onrender.com";

function App() {
  const [activeTab, setActiveTab] = useState('home');
  const [holdings, setHoldings] = useState([]);
  const [rankings, setRankings] = useState([]);
  const [portfolioPercentage, setPortfolioPercentage] = useState(null);
  const [ndaqPercentage, setNdaqPercentage] = useState(null);
  const [spyPercentage, setSpyPercentage] = useState(null);
  
  const [ticker, setTicker] = useState('');
  const [ampyfinResult, setAmpyfinResult] = useState(null);
  const [isLoading, setIsLoading] = useState(false); // Add state for loading
  const [trades, setTrades] = useState([]);
  const [theme, setTheme] = useState('dark');
  const [githubStats, setGithubStats] = useState({ stars: 0, forks: 0 });
  const [menuOpen, setMenuOpen] = useState(false);
  

  // Fetch Data from API
  const fetchPortfolioPercentage = async () => {
    try {
      const response = await axios.get(`${API_URL}/portfolio_percentage`);
      setPortfolioPercentage(response.data.portfolio_percentage);
      setNdaqPercentage(response.data.ndaq_percentage);
      setSpyPercentage(response.data.spy_percentage);
      
    } catch (error) {
      console.error('Error fetching portfolio percentage:', error);
    }
  };

  const fetchHoldings = async () => {
    try {
      const response = await axios.get(`${API_URL}/holdings`);
      setHoldings(response.data);
    } catch (error) {
      console.error('Error fetching holdings:', error);
    }
  };

  const fetchRankings = async () => {
    try {
      const response = await axios.get(`${API_URL}/rankings`);
      setRankings(response.data);
    } catch (error) {
      console.error('Error fetching rankings:', error);
    }
  };

  const fetchAmpyfinRecommendation = async (ticker) => {
    setIsLoading(true);  // Set loading to true when starting the request
    try {
      const response = await axios.get(`${API_URL}/ticker/${ticker}`);
      setAmpyfinResult(response.data);
    } catch (error) {
      console.error('Error fetching AmpyFin recommendation:', error);
      setAmpyfinResult(null);
    } finally {
      setIsLoading(false);  // Set loading to false once the request finishes
    }
  };

  const fetchRecentTrades = async () => {
    try {
      const response = await axios.get(`${API_URL}/recent_trades`);
      setTrades(response.data);
    } catch (error) {
      console.error('Error fetching recent trades:', error);
    }
  };

  const fetchGitHubStats = async () => {
    try {
      const response = await fetch('https://api.github.com/repos/yeonholee50/AmpyFin');
      const data = await response.json();
      setGithubStats({
        stars: data.stargazers_count,
        forks: data.forks_count
      });
    } catch (error) {
      console.error('Error fetching GitHub stats:', error);
    }
  };

  const toggleTheme = () => {
    const newTheme = theme === 'dark' ? 'light' : 'dark';
    setTheme(newTheme);
    document.documentElement.setAttribute('data-theme', newTheme);
    localStorage.setItem('theme', newTheme);
  };

  const toggleMenu = () => {
    setMenuOpen(!menuOpen);
  };

  useEffect(() => {
    fetchPortfolioPercentage();
    fetchHoldings();
    fetchRankings();
    fetchRecentTrades();
    fetchGitHubStats();
    const savedTheme = localStorage.getItem('theme') || 'dark';
    setTheme(savedTheme);
    document.documentElement.setAttribute('data-theme', savedTheme);
  }, []);

  const renderTabContent = () => {
    switch (activeTab) {
      case 'home':
        return (
          <div className="content-section home-section">
            <CircleAnimation />
            <div className="hero-section">
              <h1>AmpyFin</h1>
              <p className="motto">
                AI-driven precision, trustworthiness, and ability to empower both institutional and retail traders
              </p>
            </div>
          </div>
        );

      case 'how-it-works':
        return (
          <div className="content-section">
            <h2>How AmpyFin Works</h2>
            <div className="how-it-works-content">
              <p>
                AmpyFin is a cutting-edge, <strong>AI-powered trading bot</strong> designed specifically for navigating the complexities of the <strong>NASDAQ-100</strong>. By utilizing a machine learning strategy called <strong>Supervised Ensemble Learning</strong>, AmpyFin leverages a range of diverse strategies, dynamically ranking each indicator from <a href="https://ta-lib.github.io/ta-lib-python/">TA-Lib</a> based on performance and current market conditions.
              </p>
              <p>
                At the heart of AmpyFin is its <strong>dynamic ranking system</strong>, which adjusts the influence of each indicator according to its recent success rates. This system ensures that AmpyFin consistently prioritizes the most effective strategies while minimizing risk exposure.
              </p>
              <p>
                Additionally, AmpyFin utilizes a <strong>web scraping tool</strong> to retrieve and analyze current portfolio and rankings from specific trading firms. This data is ranked with recency bias and incorporated into the buy and sell decision-making process.
              </p>
              <p className="note">
                <strong>Note:</strong> Due to high volumes of requests, retrieving the data may take up to a minute. Thank you for your understanding.
              </p>
            </div>
          </div>
        );

      case 'portfolio':
        return (
          <div className="content-section">
            <h2>Current Portfolio</h2>
            <HoldingsTable holdings={holdings} />
          </div>
        );

      case 'rankings':
        return (
          <div className="content-section">
            <h2>Current Rankings</h2>
            <RankingsTable rankings={rankings} />
          </div>
        );

      case 'trades':
        return (
          <div className="content-section">
            <h2>Recent Trades</h2>
            <RecentTradesTable trades={trades} />
          </div>
        );

      case 'benchmark':
        return (
          <div className="content-section">
            <h2>Performance Benchmark</h2>
            <BenchmarkSection 
              portfolioPercentage={portfolioPercentage} 
              ndaqPercentage={ndaqPercentage} 
              spyPercentage={spyPercentage} 
            />
          </div>
        );

      case 'demo':
        return (
          <div className="content-section">
            <h2>Try AmpyFin</h2>
            <div className="demo-container">
              <p>Enter a ticker symbol to see AmpyFin's recommendation:</p>
              <form onSubmit={(e) => {
                e.preventDefault();
                fetchAmpyfinRecommendation(ticker);
              }}>
                <input 
                  type="text" 
                  value={ticker} 
                  onChange={(e) => setTicker(e.target.value)} 
                  placeholder="Enter Ticker" 
                  required 
                />
                <button type="submit" className="demo-button">Analyze</button>
              </form>

              {isLoading ? (
                <div className="loading-container">
                  <div className="spinner"></div>
                  <p>Analyzing...</p>
                </div>
              ) : ampyfinResult ? (
                <ResultContainer result={ampyfinResult} />
              ) : (
                <p className="empty-state">Enter a ticker symbol to get started</p>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="App">
      <Helmet>
        <title>AmpyFin</title>
      </Helmet>
      <header className="App-header">
        <div className="nav-container">
          <div className="logo" onClick={() => setActiveTab('home')}>AmpyFin</div>
          <nav>
            <div className="hamburger" onClick={toggleMenu}>
              &#9776;
            </div>
            <ul className={menuOpen ? 'active' : ''}>
              <li onClick={() => {setActiveTab('how-it-works'); setMenuOpen(false);}}>How It Works</li>
              <li onClick={() => {setActiveTab('portfolio'); setMenuOpen(false);}}>Portfolio</li>
              <li onClick={() => {setActiveTab('rankings'); setMenuOpen(false);}}>Current Rankings</li>
              <li onClick={() => {setActiveTab('trades'); setMenuOpen(false);}}>Recent Trades</li>
              <li onClick={() => {setActiveTab('benchmark'); setMenuOpen(false);}}>Benchmark</li>
              <li className="demo-tab" onClick={() => {setActiveTab('demo'); setMenuOpen(false);}}>Demo</li>
              <li className="github-tab">
                <span>GitHub</span>
                <div className="github-stats">
                  <div className="stat-group">
                    <div className="stat-item">
                      <span>★</span>
                      <span>{githubStats.stars}</span>
                    </div>
                    <div className="stat-item">
                      <span>𐄁</span>
                      <span>{githubStats.forks}</span>
                    </div>
                  </div>
                </div>
              </li>
            </ul>
          </nav>
          <div className="theme-toggle" onClick={toggleTheme}>
            <div className="switch-handle" style={{ transform: `translateX(${theme === 'dark' ? '0' : '22px'})` }}></div>
            <div className="sun-icon">☀️</div>
            <div className="moon-icon">🌙</div>
          </div>
        </div>
      </header>
      <main>{renderTabContent()}</main>
    </div>
  );
}

function HoldingsTable({ holdings }) {
  return (
    <div className="scrollable-table-container">
      <table className="styled-table">
        <thead>
          <tr>
            <th>Symbol</th>
            <th>Quantity</th>
          </tr>
        </thead>
        <tbody>
          {holdings.map((holding) => (
            <tr key={holding.id}>
              <td>{holding.symbol}</td>
              <td>{holding.quantity}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function RankingsTable({ rankings }) {
  return (
    <div className="scrollable-table-container">
      <table className="styled-table">
        <thead>
          <tr>
            <th>Strategy</th>
            <th>Rank</th>
          </tr>
        </thead>
        <tbody>
          {rankings.map((ranking) => (
            <tr key={ranking.id}>
              <td>{ranking.strategy}</td>
              <td>{ranking.rank}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function BenchmarkSection({ portfolioPercentage, ndaqPercentage, spyPercentage }) {
  const formatPercentage = (value) => `${value > 0 ? '+' : ''}${(value * 100).toFixed(2)}%`;

  return (
    <div className="benchmark-section">
      <div className={`portfolio-percentage ${portfolioPercentage > 0 ? 'green' : 'red'}`}>
        <span className="label">AmpyFin</span>
        <span className="value">{formatPercentage(portfolioPercentage)}</span>
        <p className="live-since">Total Percentage since Jan 3, 2025 @ 9:30 AM</p>
      </div>
      <div className={`portfolio-percentage ${ndaqPercentage > 0 ? 'green' : 'red'}`}>
        <span className="label">QQQ</span>
        <span className="value">{formatPercentage(ndaqPercentage)}</span>
        <p className="live-since">Total Percentage since Jan 3, 2025 @ 9:30 AM</p>
      </div>
      <div className={`portfolio-percentage ${spyPercentage > 0 ? 'green' : 'red'}`}>
        <span className="label">SPY</span>
        <span className="value">{formatPercentage(spyPercentage)}</span>
        <p className="live-since">Total Percentage since Jan 3, 2025 @ 9:30 AM</p>
      </div>
    </div>
  );
}

function RecentTradesTable({ trades }) {
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <div className="scrollable-table-container">
      <table className="styled-table">
        <thead>
          <tr>
            <th>Action</th>
            <th>Symbol</th>
            <th>Time</th>
          </tr>
        </thead>
        <tbody>
          {trades.map((trade) => (
            <tr key={trade.id}>
              <td className={`trade-side ${trade.side.toLowerCase()}`}>
                {trade.side}
              </td>
              <td>{trade.symbol}</td>
              <td>{formatDate(trade.time)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

const ResultContainer = ({ result }) => {
  const totalWeight = result.buy_weight + result.sell_weight + result.hold_weight;
  const buyPercentage = ((result.buy_weight / totalWeight) * 100).toFixed(1);
  const sellPercentage = ((result.sell_weight / totalWeight) * 100).toFixed(1);
  const holdPercentage = ((result.hold_weight / totalWeight) * 100).toFixed(1);

  return (
    <div className="result-container">
      <h3>{result.ticker}</h3>
      <p className={`decision-container ${result.decision}`}>
        <span className="decision-text">Decision: <strong>{result.decision}</strong></span>
        <span className="price"> @ ${result.current_price}</span>
      </p>
      <div className="weights">
        <div className="weight-item">
          <span>Buy Signal: </span>
          <span>{buyPercentage}%</span>
        </div>
        <div className="weight-item">
          <span>Sell Signal: </span>
          <span>{sellPercentage}%</span>
        </div>
        <div className="weight-item">
          <span>Hold Signal: </span>
          <span>{holdPercentage}%</span>
        </div>
      </div>
    </div>
  );
};

export default App;